import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { OFFLINE_ROUTES_CACHE_UPDATED, readOfflineRoutesSnapshot, saveOfflineRoutesSnapshot, clearAllOfflineRoutesCache, mergeOfflineRoutesSnapshot } from '../utils/offlineRoutesCache';
import { readOfflinePreferences, offlinePreferencesKey, normalizeOfflinePreferences, isOfflineAdminPlusUser, prepareAppForOffline, downloadOfflinePack, downloadOfflineDelta, checkOfflinePackVersion } from '../utils/offlineSync';

const OfflineContext = createContext(null);
const IDLE = { phase: 'idle', shellCompleted: 0, shellTotal: 0, bytes: 0, totalBytes: 0, error: '' };
// A short poll keeps rapidly changing assignments fresh while the retry backoff
// prevents a failed server from being hammered.
const SYNC_INTERVAL = 60 * 1000;

export const OfflineProvider = ({ token, userId, userType = null, networkStatus = '', children }) => {
  const effectiveUserType = userType || (typeof localStorage !== 'undefined' ? localStorage.getItem('user_type') : '') || '';
  const [preferences, setPreferences] = useState(() => readOfflinePreferences(userId, effectiveUserType));
  const [snapshot, setSnapshot] = useState(null);
  const [progress, setProgress] = useState(IDLE);
  const [modal, setModal] = useState(null);
  const running = useRef(null);
  const snapshotRef = useRef(null);
  const snapshotLoaded = useRef(false);
  const paused = useRef(false);
  const current = useRef({});
  const lastAttempt = useRef(0);
  const failures = useRef(0);
  current.current = { token, userId, userType: effectiveUserType, networkStatus, preferences };
  snapshotRef.current = snapshot;

  const cancel = useCallback(() => {
    paused.current = true;
    running.current?.abort();
    running.current = null;
    setProgress(previous => ({ ...previous, phase: 'paused', error: '' }));
  }, []);

  const refresh = useCallback(async ({ automatic = false } = {}) => {
    const state = current.current;
    if (!state.userId || !state.token || running.current) return;
    if (navigator.onLine === false || ['offline', 'server_unavailable'].includes(state.networkStatus)) return;
    if (automatic && !snapshotLoaded.current) return;
    if (automatic && (!state.preferences.enabled || paused.current || document.hidden)) return;
    if (automatic && Date.now() - lastAttempt.current < Math.min(30 * 60000, SYNC_INTERVAL * (2 ** Math.min(failures.current, 5)))) return;
    paused.current = false;
    const controller = new AbortController();
    running.current = controller;
    lastAttempt.current = Date.now();
    const isCurrent = () => running.current === controller && !controller.signal.aborted
      && localStorage.getItem('token') === state.token;
    const report = (update) => { if (isCurrent()) setProgress(previous => ({ ...previous, ...update })); };
    setProgress({ ...IDLE, phase: 'shell' });
    try {
      let shell = { ready: false, build: 'default', total: 0, completed: 0 };
      try {
        shell = await prepareAppForOffline(controller.signal, data => report({ shellCompleted: data.completed, shellTotal: data.total })) || shell;
      } catch (shellErr) {
        console.warn('prepareAppForOffline non-fatal warning:', shellErr);
      }
      if (!isCurrent()) return;
      report({ phase: 'data', shellCompleted: shell.total || 0, shellTotal: shell.total || 0 });
      const version = await checkOfflinePackVersion({ ...state, signal: controller.signal });
      const savedSnapshot = snapshotRef.current;
      const snapshotMatches = savedSnapshot?.schemaVersion === 1
        && savedSnapshot.revision === version.revision
        && (!shell.ready || savedSnapshot.shellBuild === shell.build)
        && savedSnapshot.preferences?.days === state.preferences.days
        && savedSnapshot.preferences?.scope === state.preferences.scope;
      if (snapshotMatches) {
        failures.current = 0;
        report({ phase: 'done', bytes: savedSnapshot.bytes || 0, totalBytes: savedSnapshot.bytes || 0 });
        return;
      }

      let saved = null;
      const canDoDelta = savedSnapshot?.schemaVersion === 1
        && savedSnapshot.preferences?.days === state.preferences.days
        && savedSnapshot.preferences?.scope === state.preferences.scope
        && Array.isArray(savedSnapshot.orderDetails);

      if (canDoDelta) {
        try {
          const delta = await downloadOfflineDelta({
            ...state,
            savedSnapshot,
            signal: controller.signal,
            onProgress: data => report({ bytes: data.bytes, totalBytes: data.total }),
          });
          if (!isCurrent()) return;
          report({ phase: 'saving' });
          const merged = mergeOfflineRoutesSnapshot(savedSnapshot, { ...delta, shellBuild: shell.build });
          saved = await saveOfflineRoutesSnapshot(merged, { signal: controller.signal });
        } catch (deltaErr) {
          console.warn('Delta sync fallback to full pack download:', deltaErr);
          saved = null;
        }
      }

      if (!saved) {
        const pack = await downloadOfflinePack({
          ...state, signal: controller.signal,
          onProgress: data => report({ bytes: data.bytes, totalBytes: data.total }),
        });
        if (!isCurrent()) return;
        report({ phase: 'saving', bytes: pack.bytes, totalBytes: pack.bytes });
        saved = await saveOfflineRoutesSnapshot({ ...pack, revision: version.revision, shellBuild: shell.build }, { signal: controller.signal });
      }

      if (!isCurrent()) return;
      setSnapshot(saved);
      failures.current = 0;
      report({ phase: 'done' });
      window.dispatchEvent(new CustomEvent('offline-data-ready'));
    } catch (error) {
      if (!isCurrent()) return;
      failures.current += 1;
      report({ phase: 'error', error: error.name === 'QuotaExceededError'
        ? 'На устройстве не хватает места. Освободите память и повторите загрузку.'
        : (error.message || 'Не удалось сохранить данные офлайн-режима. Повторите загрузку.') });
    } finally {
      if (running.current === controller) running.current = null;
    }
  }, []);

  const updatePreferences = useCallback((changes) => {
    const isAdmin = isOfflineAdminPlusUser(current.current.userType || snapshotRef.current?.userType);
    const next = normalizeOfflinePreferences({ ...current.current.preferences, ...changes }, isAdmin);
    try { localStorage.setItem(offlinePreferencesKey(current.current.userId), JSON.stringify(next)); }
    catch { setProgress({ ...IDLE, phase: 'error', error: 'Не удалось сохранить настройки на устройстве.' }); return; }
    running.current?.abort();
    running.current = null;
    paused.current = false;
    lastAttempt.current = 0;
    failures.current = 0;
    current.current.preferences = next;
    setPreferences(next);
    setProgress(IDLE);
  }, []);

  const clearSaved = useCallback(async () => {
    cancel();
    try {
      await clearAllOfflineRoutesCache();
      setSnapshot(null);
      setProgress(IDLE);
    } catch { setProgress({ ...IDLE, phase: 'error', error: 'Не удалось удалить сохранённые задания.' }); }
  }, [cancel]);

  useEffect(() => {
    let disposed = false;
    snapshotLoaded.current = false;
    const read = () => readOfflineRoutesSnapshot(userId).then(value => {
      if (disposed) return;
      snapshotLoaded.current = true;
      setSnapshot(value);
    }).catch(() => { snapshotLoaded.current = true; });
    const changed = (event) => {
      if (Number(event.detail?.userId) === Number(userId)) setSnapshot(event.detail);
    };
    read();
    window.addEventListener(OFFLINE_ROUTES_CACHE_UPDATED, changed);
    return () => { disposed = true; window.removeEventListener(OFFLINE_ROUTES_CACHE_UPDATED, changed); };
  }, [userId]);

  useEffect(() => {
    const run = () => refresh({ automatic: true });
    const timer = setTimeout(run, 700);
    const interval = setInterval(run, 60000);
    const onStorage = (event) => {
      if (event.key === offlinePreferencesKey(userId)) updatePreferences(readOfflinePreferences(userId, effectiveUserType));
    };
    const onOffline = () => { running.current?.abort(); running.current = null; setProgress(previous => ({ ...previous, phase: 'idle' })); };
    document.addEventListener('visibilitychange', run);
    window.addEventListener('online', run);
    window.addEventListener('offline', onOffline);
    window.addEventListener('storage', onStorage);
    return () => {
      clearTimeout(timer); clearInterval(interval);
      document.removeEventListener('visibilitychange', run);
      window.removeEventListener('online', run);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('storage', onStorage);
    };
  }, [refresh, preferences, userId, effectiveUserType, updatePreferences]);

  useEffect(() => {
    if (networkStatus === 'restored') {
      lastAttempt.current = 0;
      failures.current = 0;
      refresh({ automatic: true });
    }
  }, [networkStatus, refresh]);

  useEffect(() => {
    const handleReachable = () => {
      failures.current = 0;
    };
    window.addEventListener('network-server-reachable', handleReachable);
    return () => {
      window.removeEventListener('network-server-reachable', handleReachable);
    };
  }, []);

  useEffect(() => () => { running.current?.abort(); running.current = null; }, [token, userId]);

  const busy = ['shell', 'data', 'saving'].includes(progress.phase);
  const isAdminPlus = isOfflineAdminPlusUser(effectiveUserType || snapshot?.userType);
  return <OfflineContext.Provider value={{ preferences, updatePreferences, snapshot, progress, busy,
    networkStatus, refresh, cancel, clearSaved, modal, openModal: setModal, closeModal: () => setModal(null),
    userType: effectiveUserType, isAdminPlus }}>
    {children}
  </OfflineContext.Provider>;
};

export const useOffline = () => useContext(OfflineContext);
